<template>
  <div
    v-if="!loginInternalError"
    class="login-grid-container grid m-0 justify-content-center md:my-5"
  >
    <div class="col-12 md:col-8 lg:col-6">
      <div class="grid m-0 login-container p-3">
        <!-- Moving background -->
        <div
          class="login-bg border-round-2xl hidden md:block"
          :class="loginBgActive"
        >
          <i class="pi pi-times" @click="loginBgActive = 'first-position'"></i>
        </div>

        <!-- Form -->
        <div v-if="showExternalLogin" class="col-12 md:col-6">
          <div class="col-12">
            <h3 class="m-0">
              Iniciar sesión
              <span class="md:hidden">con su cuenta personal</span>
            </h3>
            <p class="my-2 md:my-3">
              Accede con tu cuenta para visualizar los informes
            </p>
          </div>
          <Form
            ref="formRef"
            v-bind="formConfigRef"
            tabindex="0"
            @keydown.enter="handleEnter"
          />
          <div class="col-12 flex flex-column gap-3 align-items-center mt-3">
            <Button
              label="Iniciar sesión"
              class="w-full"
              @click="handleLoginExternal"
              :loading="loading"
              loading-icon="pi pi-spinner"
            />
            <div
              class="text-xs text-gray-400 cursor-pointer"
              @click="showRecoverPasswordDialog"
            >
              Recuperar contraseña
            </div>
            <div
              class="md:hidden text-xs text-gray-400 cursor-pointer"
              @click="showExternalLogin = false"
            >
              Volver
            </div>
          </div>
        </div>

        <!-- <div v-if="showExternalLogin" class="col-12 md:hidden">
            <div class="col-12">
              <hr class="horizontal-row-primary" />
            </div>
          </div> -->

        <!-- Select login -->
        <div
          v-if="!isMobileDevice || !showExternalLogin"
          class="col-12 md:col-6"
        >
          <div class="col-12">
            <div class="flex flex-column gap-1 md:gap-3 align-items-center">
              <h3 class="m-0 text-center">
                Iniciar sesión
                <span class="md:hidden">con su cuenta del ministerio</span>
              </h3>
              <p class="my-2 md:my-3 text-center">
                Accede con tu cuenta para visualizar los informes
              </p>

              <TheUserMenu class="the-user-menu w-full" />

              <Button
                label="Cuenta personal"
                class="w-full"
                @click="toggleDefaultLogin()"
              />
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="col-12">
            <div class="login-bg border-round-2xl md:hidden"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <RecoverPasswordDialogView ref="recoverPasswordDialogView" />
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import { Form, useImmer, TheUserMenu } from "@sttedra_ui/framework";

import { setCustomAccessToken } from "@/composables/_commons/auth.js";
import useFormConfig from "@/pages/_root/views/login/login-form.view-config.js";
import { loginExternal } from "@/api/auth/auth.api.js";
import RecoverPasswordDialogView from "@/pages/_root/views/login/recover-password-dialog/RecoverPasswordDialogView.vue"; // eslint-disable-line
import { isMobile } from "@/composables/_commons/helper.js";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const loading = ref(false);
const router = useRouter();
const toast = useToast();
const formRef = ref(null);
const formConfig = ref(null);
const loginBgActive = ref("first-position");
// const loginBgActive = ref("second-position");
const { ref: formConfigRef } = useImmer(formConfig);
const recoverPasswordDialogView = ref(null);
const showExternalLogin = ref(true);
const loginInternalError = ref(false);
const isMobileDevice = ref(isMobile());
// #endregion

// #region Functions
function handleEnter() {
  handleLoginExternal();
}

async function loadForm() {
  formConfig.value = new useFormConfig(null);
}

async function handleLoginExternal() {
  const invalid = !(await formRef.value?.v$Ref.$validate());

  if (invalid) return;

  loading.value = true;

  const data = { ...formRef.value.state$$.values };

  const response = await loginExternal(data);

  if (response.status == 200) {
    const accessToken = response.data.access_token;

    localStorage.setItem("internalLogin", "true");
    localStorage.setItem("accessToken", accessToken);
    await setCustomAccessToken(accessToken);

    toast.add({
      severity: "info",
      summary: `Sesión iniciada`,
      detail:
        "Bienvenid@ a la plataforma de informes. Ahora puede visualizar los informes.",
      life: 3000,
    });

    router.push({
      name: "Public reports",
    });
  } else {
    toast.add({
      severity: "error",
      summary: "Credenciales incorrectas",
      detail:
        `Las credenciales introducidas no son correctas o la cuenta ha sido deshabilitada. 
        Por favor, pruebe de nuevo o contacte con el administrador`,
      life: 3000,
    });
  }

  loading.value = false;
}

function toggleDefaultLogin() {
  if (isMobileDevice.value) {
    showExternalLogin.value = !showExternalLogin.value;
  }

  loginBgActive.value = "second-position";
}

function showRecoverPasswordDialog() {
  recoverPasswordDialogView.value.showModal();
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  loadForm();
  const forceLogin = localStorage.getItem("forceLogin");
  const isExternal = localStorage.getItem("isExternal");

  loginInternalError.value = localStorage.getItem("loginInternalError");

  if (loginInternalError.value) {
    toast.add({
      severity: "error",
      summary: "Error al iniciar sesión",
      detail:
        "Ha ocurrido un error al intentar iniciar sesión. Por favor, inténtelo de nuevo.",
      life: 2000,
    });

    localStorage.removeItem("loginInternalError");
    return;
  }

  const interval = setInterval(() => {
    const theUserMenu = document.querySelector(".the-user-menu");

    if (theUserMenu) {
      clearInterval(interval);

      const interval2 = setInterval(() => {
        const theUserMenuButton = theUserMenu.querySelector(
          ".the-user-menu__link",
        );

        if (theUserMenuButton) {
          clearInterval(interval2);

          const theUserMenuButtonLabel =
            theUserMenuButton.querySelector(".p-button-label");

          theUserMenuButtonLabel.innerText = "Cuenta del ministerio";
        }

        if (isExternal == undefined || isExternal == "false") {
          if (forceLogin) {
            theUserMenuButton.click();
          }
        }
      }, 1);
    }
  }, 1);

  if (isMobileDevice.value) {
    showExternalLogin.value = false;
  }

  window.addEventListener("resize", () => {
    isMobileDevice.value = isMobile();
  });
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss">
@import "@/styles/_vars.scss";

.login-grid-container {
  .login-container {
    position: relative;
    background-color: #eaf6ff;
    border-radius: 1rem;
    height: 500px;
    max-height: 90vh;

    .login-bg {
      position: absolute;
      top: 0.5rem;
      left: 0.5rem;
      bottom: 0.5rem;
      width: calc(50% - 0.5rem);
      background-image: url("@/assets/images/login-bg.jpg");
      background-size: cover;
      background-position: center;
      transition: $transition;
      z-index: $login-image-z-index;

      .pi {
        position: absolute;
        top: 1rem;
        right: 1rem;
        font-size: 1.5rem;
        color: #fff;
        cursor: pointer;
        visibility: hidden;
        opacity: 0;
        transition: $transition;

        &:hover {
          color: $primary;
        }
      }

      &.second-position {
        left: 50%;

        .pi {
          visibility: visible;
          opacity: 1;
        }
      }
    }

    .the-user-menu {
      height: auto;

      .the-user-menu__link {
        width: 100%;
        border: 0 !important;
      }
    }
  }
}

@media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
  .login-grid-container {
    .login-container {
      height: auto;
      max-height: unset;
      flex-direction: column;
      min-height: var(--header-footer-height);

      .login-bg {
        position: relative;
        height: 20rem;
        width: 100%;
        top: unset;
        left: unset;
        bottom: unset;
      }
    }
  }
}
</style>
